import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { amazonConnectApi, type ApplicationConfig, type ConfigurationAuditEntry } from '../../api/amazonconnect';
import { WorkspaceSelector } from '../../components/WorkspaceSelector';
import { getWorkspaceId } from '../../lib/auth-storage';

const DEFAULT_REDIRECT = `${window.location.origin}/amazon/connect/callback`;

export function AmazonApplicationSettingsPage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState<ApplicationConfig[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<ConfigurationAuditEntry[]>([]);
  const [workspaceId, setWorkspaceId] = useState(() => getWorkspaceId() ?? '');

  const [form, setForm] = useState({
    name: 'Amazon SP-API Application',
    client_id: '',
    client_secret: '',
    environment: 'production',
    redirect_uri: DEFAULT_REDIRECT,
    region: 'EU',
    sp_api_app_id: '',
    publication_status: 'draft' as 'draft' | 'published',
    marketplace_id: 'A21TJRUUN4KGV',
  });

  const [configHealth, setConfigHealth] = useState<{
    status: string;
    score: number;
    checks: { field: string; status: string; message?: string; why?: string; how_to_fix?: string; expected?: string }[];
  } | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const selectedApp = apps.find((a) => a.id === selectedId) ?? apps[0] ?? null;

  const loadHealth = async (appId: string) => {
    try {
      const health = await amazonConnectApi.configurationHealth(appId);
      setConfigHealth(health);
    } catch {
      setConfigHealth(null);
    }
  };

  const loadAudit = async (appId: string) => {
    try {
      const entries = await amazonConnectApi.listApplicationAudit(appId);
      setAudit(entries);
    } catch {
      setAudit([]);
    }
  };

  const applyAppToForm = (app: ApplicationConfig) => {
    setForm({
      name: app.name,
      client_id: app.client_id,
      client_secret: '',
      environment: app.environment,
      redirect_uri: app.redirect_uris[0] ?? DEFAULT_REDIRECT,
      region: app.region ?? app.metadata?.region ?? 'EU',
      sp_api_app_id: app.sp_api_app_id ?? app.metadata?.sp_api_app_id ?? '',
      publication_status: app.publication_status ?? 'draft',
      marketplace_id: app.marketplace_id ?? app.metadata?.marketplace_id ?? 'A21TJRUUN4KGV',
    });
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await amazonConnectApi.listApplications();
      setApps(list);
      const active = list.filter((a) => a.status !== 'disabled');
      const current = selectedId ? list.find((a) => a.id === selectedId) : active[0] ?? list[0];
      if (current) {
        setSelectedId(current.id);
        applyAppToForm(current);
        await Promise.all([loadHealth(current.id), loadAudit(current.id)]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const payload = {
        name: form.name,
        client_id: form.client_id,
        client_secret: form.client_secret || undefined,
        environment: form.environment,
        redirect_uris: [form.redirect_uri],
        marketplace_id: form.marketplace_id,
        region: form.region,
        sp_api_app_id: form.sp_api_app_id,
        publication_status: form.publication_status,
      };
      if (!selectedApp) {
        await amazonConnectApi.createApplication(payload);
        setMessage('Amazon application configured.');
      } else {
        await amazonConnectApi.updateApplication(selectedApp.id, payload);
        setMessage('Amazon application updated.');
      }
      setForm((prev) => ({ ...prev, client_secret: '' }));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null);
    setMessage(null);
    try {
      await action();
      setMessage(successMsg);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <header>
        <div className="flex flex-wrap gap-2 text-sm text-slate-500">
          <Link to="/configuration" className="hover:text-brand-600">Configuration</Link>
          <span>/</span>
          <span>Marketplace Integrations</span>
          <span>/</span>
          <span className="text-slate-900">Amazon</span>
        </div>
        <Link to="/amazon/connect" className="mt-2 inline-block text-sm text-brand-600 hover:underline">
          ← Back to Amazon Connect
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-slate-900">Amazon Application Configuration</h1>
        <p className="mt-1 text-sm text-slate-600">
          Tenant-scoped SP-API credentials stored encrypted in PostgreSQL. Changes take effect immediately — no redeploy required.
        </p>
        <div className="mt-4 max-w-sm">
          <WorkspaceSelector
            currentId={workspaceId || undefined}
            onSwitch={(w) => {
              setWorkspaceId(w.id);
              void refresh();
            }}
          />
        </div>
      </header>

      {!workspaceId && (
        <div role="status" className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-800">
          Select a workspace before saving Amazon application credentials.
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {message && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {message}
        </div>
      )}

      {!loading && apps.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Configuration required — create your Amazon SP-API application below to enable OAuth.
        </div>
      )}

      {!loading && configHealth && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${configHealth.status === 'pass' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          <p className="font-medium">Configuration Health: {configHealth.status.toUpperCase()} ({configHealth.score}%)</p>
          <ul className="mt-2 space-y-2 text-xs">
            {configHealth.checks.map((c) => (
              <li key={c.field}>
                <div>
                  {c.status === 'pass' ? '✓' : '✗'} {c.field}{c.message ? ` — ${c.message}` : ''}
                </div>
                {c.status !== 'pass' && (c.why || c.how_to_fix || c.expected) && (
                  <div className="ml-5 mt-1 space-y-0.5 text-[11px] text-amber-800/90">
                    {c.why && <p><span className="font-medium">Why:</span> {c.why}</p>}
                    {c.how_to_fix && <p><span className="font-medium">How to fix:</span> {c.how_to_fix}</p>}
                    {c.expected && <p><span className="font-medium">Expected:</span> <span className="font-mono">{c.expected}</span></p>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {testResult && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">{testResult}</div>
      )}

      {!loading && apps.length > 1 && (
        <label className="block text-sm">
          <span className="font-medium text-slate-700">Application</span>
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={selectedId ?? ''}
            onChange={async (e) => {
              const app = apps.find((a) => a.id === e.target.value);
              if (!app) return;
              setSelectedId(app.id);
              applyAppToForm(app);
              await Promise.all([loadHealth(app.id), loadAudit(app.id)]);
            }}
          >
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} {a.status === 'disabled' ? '(disabled)' : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {!loading && (
        <form
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Field label="Application name">
            <input
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="LWA Client ID">
            <input
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              placeholder="amzn1.application-oa2-client..."
              required
            />
          </Field>
          <Field label="LWA Client Secret">
            <input
              type="password"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder={selectedApp?.has_client_secret ? '•••••••• (leave blank to keep)' : 'Required on first save'}
            />
          </Field>
          <Field label="SP-API App ID">
            <input
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              value={form.sp_api_app_id}
              onChange={(e) => setForm({ ...form, sp_api_app_id: e.target.value })}
              placeholder="amzn1.sp.solution..."
            />
            <p className="mt-1 text-xs text-slate-500">
              The Application ID from Seller Central → Develop Apps. Used as application_id on the consent URL — never the LWA Client ID.
            </p>
          </Field>
          <Field label="Publication status (Seller Central)">
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={form.publication_status}
              onChange={(e) => setForm({ ...form, publication_status: e.target.value as 'draft' | 'published' })}
            >
              <option value="draft">Draft (adds version=beta to consent URL)</option>
              <option value="published">Published</option>
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Amazon requires version=beta on the authorization URL while the app is in Draft.
              Setting this to Published while Seller Central still shows Draft causes Amazon error MD1000.
              Keep Draft until Amazon lists the app on the Selling Partner Appstore.
            </p>
            {form.publication_status === 'published' && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Warning: Publication = Published omits version=beta. If Amazon Seller Central still shows
                Draft, authorization fails with MD1000. Switch to Draft before authorizing again.
              </p>
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Region">
              <select
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
              >
                <option value="NA">North America</option>
                <option value="EU">Europe</option>
                <option value="FE">Far East</option>
              </select>
            </Field>
            <Field label="Default marketplace ID">
              <input
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                value={form.marketplace_id}
                onChange={(e) => setForm({ ...form, marketplace_id: e.target.value })}
              />
            </Field>
          </div>
          <Field label="OAuth redirect URI">
            <input
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
              value={form.redirect_uri}
              onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
              required
            />
            <p className="mt-1 text-xs text-slate-500">Must match the redirect URI registered in Seller Central.</p>
          </Field>
          <Field label="Environment">
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={form.environment}
              onChange={(e) => setForm({ ...form, environment: e.target.value })}
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </select>
          </Field>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : selectedApp ? 'Save' : 'Create configuration'}
            </button>
            {selectedApp?.id && (
              <button
                type="button"
                disabled={testing}
                onClick={async () => {
                  setTesting(true);
                  setTestResult(null);
                  try {
                    const r = await amazonConnectApi.testConnection(selectedApp.id);
                    setTestResult(
                      r.success
                        ? `Test Connection PASS (${r.score}%): ${r.message}`
                        : `Test Connection FAIL (${r.score}%): ${r.message}`,
                    );
                    await loadHealth(selectedApp.id);
                  } catch (e) {
                    setTestResult(e instanceof Error ? e.message : 'Test failed');
                  } finally {
                    setTesting(false);
                  }
                }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
            )}
            {selectedApp?.status === 'disabled' ? (
              <button
                type="button"
                onClick={() =>
                  void runAction(async () => {
                    await amazonConnectApi.activateApplication(selectedApp.id);
                  }, 'Application activated.')
                }
                className="rounded-lg border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
              >
                Activate
              </button>
            ) : selectedApp ? (
              <button
                type="button"
                onClick={() =>
                  void runAction(async () => {
                    await amazonConnectApi.disableApplication(selectedApp.id);
                  }, 'Application disabled.')
                }
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Disable
              </button>
            ) : null}
            {selectedApp && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    void runAction(async () => {
                      await amazonConnectApi.duplicateApplication(selectedApp.id);
                    }, 'Application duplicated.')
                  }
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Delete this Amazon application configuration?')) {
                      void runAction(async () => {
                        await amazonConnectApi.deleteApplication(selectedApp.id);
                        setSelectedId(null);
                      }, 'Application deleted.');
                    }
                  }}
                  className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Delete
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => navigate('/amazon/connect')}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Return to wizard
            </button>
          </div>
        </form>
      )}

      {!loading && audit.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">Audit trail</h2>
          <ul className="mt-2 max-h-48 space-y-2 overflow-y-auto text-xs text-slate-600">
            {audit.map((e) => (
              <li key={e.id} className="border-b border-slate-100 pb-2">
                <span className="font-medium text-slate-800">{e.action}</span>
                {e.actor ? ` · ${e.actor}` : ''} · {new Date(e.created_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

